import React from 'react';
import { useField } from 'formik';
import { useSchemaContext } from './SparkForm';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  FormHelperText,
  Grid,
  GridProps,
  InputLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useResize } from 'hooks/common';
import type { InputProps, TextFieldProps } from '@mui/material';
import { isJSON } from 'utilities/index';
import { NUMERIC_TWO_DECIMAL_REGEX } from 'constants/regex';
import {
  Facebook,
  Twitter,
  YouTube,
  LinkedIn,
  WebStories,
  WebTwoTone,
  WebStoriesSharp,
} from '@mui/icons-material';
import WebsiteIcon from 'components/SVG/WebsiteIcon';

export type SparkTextFieldProps = {
  name: string;
  label: string | React.ReactNode;
  placeholder?: string;
  size?: GridProps['xs'];
  type?: TextFieldProps['type'];
  readonly?: boolean;
  sx?: TextFieldProps['sx'];
  startAdornment?: InputProps['startAdornment'];
  endAdornment?: InputProps['endAdornment'];
  helpText?: string;
  subHeaderText?: string;
  autoLowerCase?: boolean;
  hideHelperText?: boolean;
  containerStyle?: GridProps['sx'];
  maxNumber?: number;
  showCounter?: boolean;
  required?: boolean;
  socialMediaIcon?: 'facebook' | 'twitter' | 'youtube' | 'linkedin' | 'website';
} & Omit<TextFieldProps, 'sx' | 'children' | 'size'>;

const SparkTextField = React.forwardRef(function SparkTextField(
  {
    name,
    label,
    placeholder,
    size,
    type = 'text',
    readonly = false,
    sx,
    startAdornment = <></>,
    endAdornment = <></>,
    helpText = '',
    subHeaderText = '',
    autoLowerCase = false,
    hideHelperText = false,
    containerStyle,
    maxNumber,
    showCounter = false,
    required = false,
    socialMediaIcon,
    onChange,
    ...rest
  }: SparkTextFieldProps,
  ref: React.Ref<HTMLDivElement>
) {
  const [field, meta, helper] = useField<string>(name);
  const { validationSchema } = useSchemaContext();
  const { isMobile } = useResize();
  const isFieldRequired = React.useMemo(() => {
    try {
      const fieldSchema = validationSchema.describe().fields[name];
      // @ts-ignore
      const isRequiredSchema = fieldSchema.tests.find(
        //@ts-ignore
        (test) => test.name === 'requiredTest'
      );
      if (isRequiredSchema) {
        return true;
      }

      if ('optional' in fieldSchema) {
        return !fieldSchema.optional;
      }
      return false;
    } catch {
      return false;
    }
  }, [name, validationSchema]);

  const counter = React.useMemo(() => {
    const fieldSchema = validationSchema.describe().fields[name];
    // @ts-ignore
    const minTest = fieldSchema?.tests.find(
      //@ts-ignore
      (test) => test.name === 'min'
    );
    // @ts-ignore
    const maxTest = fieldSchema?.tests.find(
      //@ts-ignore
      (test) => test.name === 'max'
    );
    return { min: minTest?.params?.min, max: maxTest?.params?.max };
  }, [name, validationSchema]);

  const isError = meta.touched && !!meta.error;

  const defaultSize = size || (isMobile ? 12 : 6);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange && onChange(event);
    // if (type === 'date') {
    //   const date = new Date(event.target.value);
    //   helper.setValue(date.toISOString());
    // }
    if (type !== 'number') {
      if (event.target.value.length > counter.max) return;
      field.onChange(event);
    }

    const inputValue = event.target.value;

    if (maxNumber && +inputValue > maxNumber) {
      return;
    }

    if (NUMERIC_TWO_DECIMAL_REGEX.test(inputValue)) {
      helper.setValue(inputValue);
    }
  };

  const getValue = (value: string) => {
    if (autoLowerCase) {
      return field.value.toLowerCase();
    }
    if (isJSON(field.value)) {
      return JSON.stringify(JSON.parse(field.value), null, 2);
    }
    return field.value;
  };

  // Determine the social media icon to display before the placeholder
  const getSocialMediaIcon = () => {
    switch (socialMediaIcon) {
      case 'website':
        return (
          <WebsiteIcon style={{ marginRight: 8, width: 22, height: 22 }} />
        );
      case 'facebook':
        return <Facebook fontSize='medium' color='primary' sx={{ mr: 1 }} />;
      case 'twitter':
        return <Twitter fontSize='medium' color='primary' sx={{ mr: 1 }} />;
      case 'youtube':
        return <YouTube fontSize='medium' color='primary' sx={{ mr: 1 }} />;
      case 'linkedin':
        return <LinkedIn fontSize='medium' color='primary' sx={{ mr: 1 }} />;
      default:
        return null;
    }
  };

  return (
    <Grid
      item
      xs={defaultSize}
      container
      direction='column'
      sx={containerStyle}
    >
      <Box display='flex' flexDirection='column'>
        {label && (
          <InputLabel
            sx={{
              marginLeft: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: 16,
              width: '100%',
            }}
          >
            <span>{(isFieldRequired || required) && '*'}</span>
            {label}
            {helpText && (
              <Tooltip
                title={helpText}
                placement='top'
                arrow
                enterTouchDelay={0}
              >
                <InfoOutlinedIcon fontSize='small' />
              </Tooltip>
            )}
            <Box sx={{ flexGrow: 1 }} />
            {showCounter && counter.max && (
              <Typography
                fontSize={12}
                textAlign='right'
                pr={1.6}
                sx={{
                  color: (theme) =>
                    isError ? `${theme.palette.error.main}` : '#A3A3A3',
                }}
              >
                {field.value.length}/{counter.max}
              </Typography>
            )}
          </InputLabel>
        )}

        {subHeaderText && (
          <Typography
            color='#A3A3A3'
            marginLeft={1.6}
            fontSize={12}
            textAlign='left'
          >
            {subHeaderText}
          </Typography>
        )}
      </Box>

      <TextField
        type={type}
        ref={ref}
        style={{ border: 'unset' }}
        placeholder={placeholder || ''}
        disabled={readonly}
        fullWidth
        error={isError}
        autoComplete='off'
        InputProps={{
          readOnly: readonly,
          startAdornment: socialMediaIcon
            ? getSocialMediaIcon()
            : startAdornment, // Display social media icon if provided
          endAdornment,
        }}
        InputLabelProps={{
          shrink: true,
        }}
        inputProps={{
          min: 0,
          max: maxNumber,
        }}
        sx={{
          '& .MuiInputBase-multiline': {
            alignItems: 'flex-start',
          },

          ...sx,
        }}
        {...field}
        onChange={handleChange}
        value={getValue(field.value)}
        {...rest}
      />
      {!hideHelperText && (
        <FormHelperText
          sx={{
            color: (theme) => theme.palette.error.main,
            marginLeft: 1,
            marginBottom: 0.5,
          }}
        >
          {isError ? meta.error : ' '}
        </FormHelperText>
      )}
    </Grid>
  );
});

export default SparkTextField;
